import type { Room } from "livekit-client";

export interface TransportCommandEnvelope {
  readonly op: string;
  readonly commandId?: string;
  readonly executeAtServerMonoMs?: number;
  readonly authoritativeExecuteAtMs?: number;
  readonly payload?: Record<string, unknown>;
}

export type LivekitTransportEvent =
  | { kind: "connected"; participantId: string }
  | { kind: "disconnected"; reason?: string }
  | { kind: "participants_snapshot"; participantIds: readonly string[] }
  | { kind: "participant_connected"; participantId: string }
  | { kind: "participant_disconnected"; participantId: string }
  | { kind: "command"; command: TransportCommandEnvelope };

export interface LivekitTransport {
  connect(): Promise<void>;
  disconnect(reason?: string): void;
  publish(command: TransportCommandEnvelope): void;
  onEvent(listener: (event: LivekitTransportEvent) => void): () => void;
}

export interface LivekitTransportOptions {
  readonly roomName: string;
  readonly participantId: string;
  readonly token?: string;
  readonly livekitUrl?: string;
}

export class MockLivekitTransport implements LivekitTransport {
  private readonly listeners = new Set<(event: LivekitTransportEvent) => void>();
  private connected = false;
  private readonly participantId: string;
  readonly sentCommands: TransportCommandEnvelope[] = [];

  constructor(options: LivekitTransportOptions) {
    this.participantId = options.participantId;
  }

  connect(): Promise<void> {
    this.connected = true;
    this.emit({ kind: "connected", participantId: this.participantId });
    return Promise.resolve();
  }

  disconnect(reason?: string): void {
    if (!this.connected) {
      return;
    }
    this.connected = false;
    if (reason === undefined) {
      this.emit({ kind: "disconnected" });
      return;
    }
    this.emit({ kind: "disconnected", reason });
  }

  publish(command: TransportCommandEnvelope): void {
    if (!this.connected) {
      return;
    }
    this.sentCommands.push(command);
  }

  onEvent(listener: (event: LivekitTransportEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  simulateIncoming(command: TransportCommandEnvelope): void {
    if (!this.connected) {
      return;
    }
    this.emit({ kind: "command", command });
  }

  private emit(event: LivekitTransportEvent): void {
    for (const listener of this.listeners) {
      listener(event);
    }
  }
}

/**
 * Browser transport backed by livekit-client data channels.
 * Keeps scope intentionally minimal for lab bring-up.
 */
export class LivekitClientTransport implements LivekitTransport {
  private readonly listeners = new Set<(event: LivekitTransportEvent) => void>();
  private readonly options: LivekitTransportOptions;
  private room: Room | null = null;
  private connected = false;

  constructor(options: LivekitTransportOptions) {
    this.options = options;
  }

  async connect(): Promise<void> {
    if (!this.options.token || !this.options.livekitUrl) {
      throw new Error("LiveKit token and URL are required for live transport");
    }

    const { Room, DataPacket_Kind, RoomEvent } = await import("livekit-client");
    const room = new Room();

    room.on(RoomEvent.Connected, () => {
      this.connected = true;
      this.emit({
        kind: "connected",
        participantId: this.options.participantId,
      });
      const remoteParticipantIds = Array.from(room.remoteParticipants.values())
        .map((participant) => participant.identity)
        .filter((participantId) => participantId.length > 0);
      if (remoteParticipantIds.length > 0) {
        this.emit({
          kind: "participants_snapshot",
          participantIds: remoteParticipantIds,
        });
      }
    });

    room.on(RoomEvent.Disconnected, (reason) => {
      this.connected = false;
      this.emit({
        kind: "disconnected",
        reason: String(reason ?? "unknown"),
      });
    });

    room.on(RoomEvent.DataReceived, (payload) => {
      try {
        const decoded = new TextDecoder().decode(payload);
        const parsed = JSON.parse(decoded) as TransportCommandEnvelope;
        this.emit({ kind: "command", command: parsed });
      } catch {
        // Ignore malformed data packets in lab mode.
      }
    });

    room.on(RoomEvent.ParticipantConnected, (participant) => {
      this.emit({
        kind: "participant_connected",
        participantId: participant.identity,
      });
    });

    room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      this.emit({
        kind: "participant_disconnected",
        participantId: participant.identity,
      });
    });

    await room.connect(this.options.livekitUrl, this.options.token, {
      autoSubscribe: true,
    });

    // Touch enum so tree-shaking keeps packet kind and connect succeeds in strict builds.
    void DataPacket_Kind.RELIABLE;
    this.room = room;
  }

  disconnect(reason?: string): void {
    if (!this.room) {
      return;
    }
    // disconnect returns a promise in newer livekit-client; lab mode fires & forgets.
    void this.room.disconnect();
    this.room = null;
    this.connected = false;
    if (reason === undefined) {
      this.emit({ kind: "disconnected" });
      return;
    }
    this.emit({
      kind: "disconnected",
      reason,
    });
  }

  publish(command: TransportCommandEnvelope): void {
    if (!this.connected || !this.room?.localParticipant) {
      return;
    }
    const payload = new TextEncoder().encode(JSON.stringify(command));
    // Ignore returned promise in lab mode; telemetry is in UI rails.
    void this.room.localParticipant.publishData(payload, {
      reliable: true,
      topic: "transport.commands",
    });
  }

  onEvent(listener: (event: LivekitTransportEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(event: LivekitTransportEvent): void {
    for (const listener of this.listeners) {
      listener(event);
    }
  }
}
